/**
 * Description Section Component for Product Feature Display
 * 
 * A flexible content section component that combines rich text descriptions
 * with high-quality imagery to showcase product features, technical details,
 * and craftsmanship. Features intelligent image fallback and responsive layouts.
 * 
 * Layout Variations:
 * - Left-aligned images with right-side text content
 * - Right-aligned images with left-side text content
 * - Responsive stacking on mobile devices
 * - Consistent spacing and visual hierarchy
 * 
 * Image Management:
 * - Dynamic image loading from Sanity CMS
 * - Category-specific fallback image system
 * - Error handling and automatic fallback switching
 * - Next.js Image optimization for performance
 * - Responsive image sizing and aspect ratios
 * 
 * Content Features:
 * - Rich text rendering via Portable Text
 * - Brand-consistent typography and styling
 * - Flexible content structure from CMS
 * - Mobile-optimized text hierarchy
 * - Accessible content structure
 * 
 * Fallback Strategy:
 * - MTB: Mountain bike specific imagery
 * - Road: Road bike specific imagery  
 * - Gravel: Gravel bike specific imagery
 * - Intelligent rotation based on section index
 * - Graceful degradation for missing images
 * 
 * Performance Optimizations:
 * - Lazy loading for images and content
 * - Efficient state management
 * - Progressive enhancement approach
 * - Optimized re-rendering patterns
 * 
 * Responsive Design:
 * - Mobile-first approach with stacked layout
 * - Tablet and desktop side-by-side layouts
 * - Adaptive image sizing and positioning
 * - Consistent spacing across breakpoints
 * 
 * @param {string} title - Section title/heading
 * @param {Array} description - Rich text content array from Sanity
 * @param {object} image - Sanity image object
 * @param {string} imagePosition - Image alignment ('left' or 'right')
 * @param {number} index - Section index for fallback image selection
 * @param {string} category - Bike category for fallback image selection
 * 
 * @description Flexible content section with rich text and intelligent image fallback system
 */

import Image from 'next/image';
import { Suspense } from 'react';
import { DynamicPortableTextWithComponents } from '../../utils/dynamicImports';
import { urlForImage } from '../../src/app/studio/lib/image';
import { sanityLoader } from '../../utils/sanityLoader';
import portableTextComponents from '../ui/PortableTextComponents';  
import { useState, useEffect } from 'react';

/**
 * Get fallback images based on section number and bike category
 * Provides intelligent image selection for consistent visual experience
 */
const getFallbackImage = (index, title, category) => {
  // Convert category to lowercase for consistent path handling
  const categoryLower = category?.toLowerCase() || 'mtb';
  
  // Default fallbacks by category and section index
  const fallbacks = {
    mtb: [
      '/images/mtb-description1.jpg',
      '/images/mtb-description2.jpg',
      '/images/mtb-description3.jpg',
      '/images/mtb-description4.jpg',
    ],
    gravel: [
      '/images/gravel-description1.jpeg',
      '/images/gravel-description2.jpeg',
      '/images/gravel-description3.jpeg',
      '/images/gravel-description4.jpeg',
    ],
    road: [
      '/images/road-description-Cockpit.jpg',
      '/images/road-description-detail.jpg',
      '/images/road-description-rear.jpg',
      '/images/road-grooty1-product.jpg',
    ]
  };
  
  // Determine which image to use based on index and fallbacks available
  const categoryFallbacks = fallbacks[categoryLower] || fallbacks.mtb;
  return categoryFallbacks[index % categoryFallbacks.length];
};

// Description Section Component
const DescriptionSection = ({ title, description, image, imagePosition, index, category = 'mtb' }) => {
  const [imageError, setImageError] = useState(false);
  const [processedImageUrl, setProcessedImageUrl] = useState(null);
  const [fallbackImageUrl, setFallbackImageUrl] = useState(null);

  // Set fallback image based on section index and bike category
  useEffect(() => {
    setFallbackImageUrl(getFallbackImage(index, title, category));
  }, [index, title, category]);

  // Process image URL on mount and when image changes
  useEffect(() => {
    const processImage = () => {
      if (!image) {
        console.log(`Section "${title}": No image provided, using fallback`);
        return null;
      }
      
      try {
        // Handle string URLs
        if (typeof image === 'string') {
          return image;
        }
        
        // Handle Sanity image references
        if (image.asset && (image.asset._ref || image.asset._id)) {
          try {
            const imageUrl = urlForImage(image);
            return imageUrl;
          } catch (err) {
            console.error(`Failed to process Sanity image:`, err);
            return null;
          }
        }
        
        // Handle direct URL in image object
        if (image.url) {
          return image.url;
        }

        // Handle nested image inside object
        if (image.image && image.image.asset) {
          try {
            const imageUrl = urlForImage(image.image);
            return imageUrl;
          } catch (err) {
            console.error(`Failed to process nested Sanity image:`, err);
            return null;
          }
        }

        return null;
      } catch (err) {
        console.error(`Error processing image:`, err);
        return null;
      }
    };

    const result = processImage();
    setProcessedImageUrl(result);
  }, [image, title]);

  return (
    <div 
      className={`flex flex-col mt-16 md:flex-row md:items-center md:gap-10 ${
        imagePosition === 'right' ? 'md:flex-row-reverse' : 
        imagePosition === 'left' ? '' : 
        (index % 2 !== 0) ? 'md:flex-row-reverse' : ''
      }`}
    >
      {/* Image */}
      <div className="md:w-1/2 mb-6 md:mb-0">
        <div className="relative h-64 md:h-96 rounded-lg overflow-hidden bg-gray-100 shadow-md hover:shadow-xl transition-all duration-300">
          {processedImageUrl && !imageError ? (
            <Image
              src={processedImageUrl}
              alt={title || `Section ${index + 1}`}
              fill
              loader={sanityLoader}
              sizes="(max-width: 768px) 100vw, 50vw"
              className="object-cover"
              priority={index === 0}
              onError={(e) => {
                console.log(`Image load error for ${title}, using fallback`);
                setImageError(true);
              }}
            />
          ) : fallbackImageUrl ? (
            <Image
              src={fallbackImageUrl}
              alt={title || `Section ${index + 1} (Fallback)`}
              fill
              loader={sanityLoader}
              sizes="(max-width: 768px) 100vw, 50vw"
              className="object-cover"
              priority={index === 0}
              onError={(e) => {
                console.error(`Even fallback image failed: ${fallbackImageUrl}`);
              }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-200">
              <p className="text-gray-500">No image available</p>
            </div>
          )}
        </div>
      </div>
      
      {/* Text */}
      <div className="md:w-1/2">
        <h3 className="text-2xl md:text-2xl font-bold mb-6 font-neuropol">{title}</h3>
        <div className="w-16 h-1 bg-accent-color mb-6"></div>
        <div className="text-gray-700 text-lg leading-relaxed">
          {typeof description === 'string' ? (
            <p>{description}</p>
          ) : (
            <Suspense fallback={<div className="animate-pulse h-4 bg-gray-200 rounded"></div>}>
              <DynamicPortableTextWithComponents value={description} components={portableTextComponents} />
            </Suspense>
          )}
        </div>
      </div>
    </div>
  );
};

export default DescriptionSection; 